/**
 * OS permission hook.
 *
 * OS permissions (location, push, camera, ...) are a distinct kind of setting:
 * the operating system owns the value, so they do not fit the value + setter
 * interface of `useSetting`. This hook mirrors the current grant status and
 * offers the right next action — request when never asked, otherwise a launcher
 * out to the OS Settings app (or, on web, denied guidance).
 *
 * Today only `location` exists; the generic signature keeps callers stable as
 * more permissions are added. The location logic is platform-split behind
 * `use-permission.location` (native reads expo-location via the shared
 * `@/utils/location-source`; web reads `navigator.permissions`).
 */
import { useLocationPermissionSetting } from './use-permission.location';

export type PermissionKey = 'location';

export type PermissionStatus = 'granted' | 'denied' | 'undetermined';

export interface UsePermissionResult {
  status: PermissionStatus;
  request: () => Promise<void>;
  openSettings: () => Promise<void>;
  /** Whether the platform can deep-link to a settings screen (true native, false web). */
  canOpenSettings: boolean;
  /** Short instruction for the denied path where there is no settings deep-link (web). */
  deniedGuidance?: string;
}

export function usePermission(_key: PermissionKey): UsePermissionResult {
  // `location` is the only key today, so delegate unconditionally. When more
  // permissions arrive, switch on `_key` — each still resolves through its own
  // platform-split hook.
  return useLocationPermissionSetting();
}
